import { Link, useParams } from "react-router-dom";
import { useSeo } from "@/hooks/useSeo";
import { getChannel } from "@/mocks/channels";
import { articles, getArticle } from "@/mocks/articles";
import { formatDateCN, formatViews, toneClasses } from "@/lib/ui";
import Breadcrumb from "@/components/feature/Breadcrumb";
import ArticleCard from "@/components/feature/ArticleCard";
import ChannelSidebar from "@/components/feature/ChannelSidebar";

const SITE = "https://zozxnbl.cn";

export default function Wenzhang() {
  const { slug = "" } = useParams();
  const article = getArticle(slug);
  const channel = article ? getChannel(article.channel) : undefined;

  const headings = (article?.sections ?? [])
    .map((s, i) => ({ heading: s.heading, id: `section-${i}` }))
    .filter((h): h is { heading: string; id: string } => Boolean(h.heading));

  const related = article
    ? articles.filter((a) => a.channel === article.channel && a.slug !== article.slug).slice(0, 3)
    : [];

  useSeo({
    title: article ? `${article.title} | 51爆料网` : "文章 | 51爆料网",
    description: article ? article.excerpt : "51爆料网文章详情页。",
    keywords: article ? article.tags.join(",") : "51爆料网",
    canonical: `/wenzhang/${slug}`,
    ogType: "article",
    jsonLd: article
      ? [
          {
            "@context": "https://schema.org",
            "@type": "Article",
            headline: article.title,
            description: article.excerpt,
            image: article.cover,
            datePublished: article.date,
            dateModified: article.date,
            articleSection: channel?.name,
            keywords: article.tags.join(","),
            author: { "@type": "Organization", name: `51爆料网${article.authorRole.replace("51爆料网", "")}` },
            publisher: { "@type": "Organization", name: "51爆料网" },
            mainEntityOfPage: `${SITE}/wenzhang/${article.slug}`,
          },
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "首页", item: SITE },
              { "@type": "ListItem", position: 2, name: "栏目导航", item: `${SITE}/lanmu` },
              {
                "@type": "ListItem",
                position: 3,
                name: channel?.name ?? "",
                item: `${SITE}/pindao/${article.channel}`,
              },
              {
                "@type": "ListItem",
                position: 4,
                name: article.title,
                item: `${SITE}/wenzhang/${article.slug}`,
              },
            ],
          },
        ]
      : undefined,
  });

  if (!article || !channel) {
    return (
      <div className="mx-auto max-w-[1280px] px-6 py-24 text-center">
        <i className="ri-file-warning-line text-5xl text-primary-500" />
        <h1 className="mt-4 font-heading text-2xl font-bold text-foreground-950">未找到该内容</h1>
        <p className="mt-2 text-sm text-foreground-600">这篇文章可能已调整或移动，请从栏目导航重新查找。</p>
        <Link
          to="/lanmu"
          className="mt-6 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
        >
          <i className="ri-compass-3-line" />
          前往栏目导航
        </Link>
      </div>
    );
  }

  const tone = toneClasses[channel.tone];

  return (
    <article className="mx-auto max-w-[1280px] px-6 py-8 md:py-10">
      <Breadcrumb
        items={[
          { label: "栏目导航", to: "/lanmu" },
          { label: channel.name, to: `/pindao/${channel.slug}` },
          { label: "正文" },
        ]}
      />

      <div className="mt-6 grid grid-cols-1 gap-10 lg:grid-cols-12">
        <div className="lg:col-span-8">
          <header>
            <Link
              to={`/pindao/${channel.slug}`}
              className={`inline-flex items-center gap-1.5 rounded px-2.5 py-1 text-xs font-medium ${tone.bgSoft} ${tone.textStrong}`}
            >
              <i className={`${channel.icon}`} />
              {channel.name}
            </Link>
            <h1 className="mt-4 font-heading text-2xl font-black leading-snug text-foreground-950 md:text-4xl">
              {article.title}
            </h1>
            <p className="mt-4 text-base leading-relaxed text-foreground-600">{article.excerpt}</p>
            <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 border-y border-background-200 py-3 text-xs text-foreground-500">
              <span className="flex items-center gap-1.5">
                <i className="ri-user-3-line" />
                {article.author} · {article.authorRole}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-calendar-line" />
                {formatDateCN(article.date)}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-time-line" />
                {article.readTime} 分钟阅读
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-eye-line" />
                {formatViews(article.views)}阅读
              </span>
            </div>
          </header>

          <figure className="mt-6 overflow-hidden rounded-lg border border-background-200">
            <div className="h-64 w-full bg-background-200 md:h-96">
              <img
                src={article.cover}
                alt={`${article.title} 配图`}
                title={`${article.title} · 51爆料网`}
                className="h-full w-full object-top"
              />
            </div>
            <figcaption className="border-t border-background-200 bg-background-100 px-4 py-2.5 text-xs text-foreground-500">
              配图与本文主题相关 · 51爆料网{channel.name}
            </figcaption>
          </figure>

          <div className="mt-8">
            {article.sections.map((section, index) => {
              const id = `section-${index}`;
              return (
                <section key={id} id={id} className="mb-9 scroll-mt-28">
                  {section.heading && (
                    <h2 className="mb-4 flex items-center gap-3 font-heading text-xl font-bold text-foreground-950 md:text-2xl">
                      <span className="h-5 w-1.5 rounded-full bg-primary-500" />
                      {section.heading}
                    </h2>
                  )}
                  {section.paragraphs?.map((p) => (
                    <p key={p} className="mb-4 text-[15px] leading-loose text-foreground-700">
                      {p}
                    </p>
                  ))}
                  {section.list && (
                    <ul className="mb-4 space-y-2.5 rounded-lg border border-background-200 bg-background-100 p-4">
                      {section.list.map((item) => (
                        <li key={item} className="flex items-start gap-2.5 text-[15px] leading-relaxed text-foreground-700">
                          <i className="ri-checkbox-circle-line mt-0.5 shrink-0 text-accent-600" />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                  {section.quote && (
                    <blockquote className="mb-4 border-l-4 border-primary-400 bg-background-100 px-5 py-4 font-heading text-base font-medium leading-relaxed text-foreground-800">
                      {section.quote}
                    </blockquote>
                  )}
                </section>
              );
            })}
          </div>

          <div className="flex flex-wrap items-center gap-2 border-t border-background-200 pt-5">
            <span className="text-xs text-foreground-500">标签：</span>
            {article.tags.map((tag) => (
              <span key={tag} className="rounded-full bg-secondary-100 px-3 py-1 text-xs text-secondary-900">
                {tag}
              </span>
            ))}
          </div>

          <div className="mt-8 rounded-lg border border-background-200 bg-background-100 p-5">
            <p className="text-sm leading-relaxed text-foreground-600">
              <i className="ri-information-line mr-1.5 text-accent-600" />
              本文为信息整理与观察记录，不构成专业建议。如发现内容与事实存在出入，欢迎通过
              <a href="mailto:hello@51baoliao.com" className="mx-1 text-primary-600 hover:underline">
                hello@51baoliao.com
              </a>
              反馈，我们会核实后补充说明。
            </p>
          </div>
        </div>

        <aside className="lg:col-span-4">
          {headings.length > 0 && (
            <div className="rounded-lg border border-background-200 bg-background-50 p-4">
              <h2 className="font-heading text-base font-bold text-foreground-950">本文目录</h2>
              <nav className="mt-3 space-y-1" aria-label="文章目录">
                {headings.map((h) => (
                  <a
                    key={h.id}
                    href={`#${h.id}`}
                    className="flex items-start gap-2 rounded-md px-2 py-1.5 text-sm text-foreground-600 transition-colors hover:bg-background-100 hover:text-primary-700"
                  >
                    <i className="ri-subtract-line mt-1 shrink-0 text-primary-400" />
                    {h.heading}
                  </a>
                ))}
              </nav>
            </div>
          )}

          <div className="mt-6">
            <h2 className="mb-3 font-heading text-base font-bold text-foreground-950">相关阅读</h2>
            <div className="space-y-3">
              {related.length > 0 ? (
                related.map((a) => <ArticleCard key={a.slug} article={a} variant="row" />)
              ) : (
                <p className="rounded-lg border border-dashed border-background-300 px-4 py-6 text-center text-sm text-foreground-500">
                  该板块暂无更多相关文章。
                </p>
              )}
            </div>
          </div>

          <div className="mt-6">
            <ChannelSidebar />
          </div>
        </aside>
      </div>
    </article>
  );
}